import React, { useState, useEffect, useCallback } from 'react';
import { useNavStep } from '../lib/NavHistory';

const COLORS = {
  primary: '#2c7a7b',
  primaryLight: '#e6fffa',
  dark: '#1a202c',
  text: '#4a5568',
  textLight: '#718096',
  border: '#e2e8f0',
  white: '#ffffff',
  bg: '#f7fafc',
  green: '#38a169',
  blue: '#3182ce',
};

const COMMON_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
  { code: 'he', name: 'Hebrew', flag: '🇮🇱' },
  { code: 'th', name: 'Thai', flag: '🇹🇭' },
  { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
  { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' },
  { code: 'cs', name: 'Czech', flag: '🇨🇿' },
  { code: 'ro', name: 'Romanian', flag: '🇷🇴' },
  { code: 'hu', name: 'Hungarian', flag: '🇭🇺' },
  { code: 'el', name: 'Greek', flag: '🇬🇷' },
  { code: 'da', name: 'Danish', flag: '🇩🇰' },
  { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
  { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'ms', name: 'Malay', flag: '🇲🇾' },
];

export default function TranslationPanel({ isOpen, onClose }) {
  // #65 Phase C1: global Back closes this overlay first. Unconditional hook,
  // generic label, never PHI.
  useNavStep({ active: isOpen, label: 'Translation', onBack: () => onClose && onClose() });
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [targetLang, setTargetLang] = useState('es');
  const [sourceLang, setSourceLang] = useState('auto');
  const [detectedLang, setDetectedLang] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [showAllLangs, setShowAllLangs] = useState(false);

  useEffect(() => {
    setCharCount(sourceText.length);
  }, [sourceText]);

  const handleDetectLanguage = useCallback(async (text) => {
    if (!text || text.length < 10) return;
    const api = window.zignagenixAPI;
    if (!api) return;
    setDetecting(true);
    try {
      const result = await api.detectLanguage(text);
      if (result && result.language !== 'unknown') {
        setDetectedLang(result);
      }
    } catch (e) {
      // Ignore detect errors
    } finally {
      setDetecting(false);
    }
  }, []);

  // Auto-detect language after typing stops
  useEffect(() => {
    if (sourceLang !== 'auto' || sourceText.length < 10) return;
    const timer = setTimeout(() => handleDetectLanguage(sourceText), 800);
    return () => clearTimeout(timer);
  }, [sourceText, sourceLang, handleDetectLanguage]);

  const handleTranslate = async () => {
    if (!sourceText.trim()) return;
    setTranslating(true);
    setError(null);
    setTranslatedText('');
    try {
      const api = window.zignagenixAPI;
      if (!api) { setError('API not available'); return; }
      const targetName = COMMON_LANGUAGES.find(l => l.code === targetLang)?.name || targetLang;
      const result = await api.translateText(sourceText, targetName, sourceLang === 'auto' ? 'auto' : (COMMON_LANGUAGES.find(l => l.code === sourceLang)?.name || sourceLang));
      if (result && result.error) {
        setError(result.message);
      } else if (result && result.translatedText) {
        setTranslatedText(result.translatedText);
      } else {
        setError('No translation returned');
      }
    } catch (err) {
      setError('Translation failed: ' + (err.message || 'Unknown error'));
    } finally {
      setTranslating(false);
    }
  };

  const handleSwapLanguages = () => {
    if (sourceLang === 'auto') return;
    const tempLang = sourceLang;
    setSourceLang(targetLang);
    setTargetLang(tempLang);
    setSourceText(translatedText);
    setTranslatedText(sourceText);
  };

  const handleCopyTranslation = () => {
    if (!translatedText) return;
    navigator.clipboard.writeText(translatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    setSourceText('');
    setTranslatedText('');
    setDetectedLang(null);
    setError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleTranslate();
    }
  };

  if (!isOpen) return null;

  const displayedLangs = showAllLangs ? COMMON_LANGUAGES : COMMON_LANGUAGES.slice(0, 12);

  return (
    <div style={{
      position: 'fixed', bottom: 80, right: 20, width: 440, maxHeight: 'calc(100vh - 120px)',
      background: COLORS.white, borderRadius: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      zIndex: 10000, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      border: `1px solid ${COLORS.border}`,
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 18px', background: 'linear-gradient(135deg, #2c7a7b, #285e61)',
        color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🌐</span>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Translate</span>
          <span style={{ fontSize: 10, opacity: 0.8, background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 10 }}>Claude AI</span>
        </div>
        <button onClick={onClose} style={{
          background: 'rgba(255,255,255,0.2)', border: 'none', color: COLORS.white,
          width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', fontSize: 14, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>✕</button>
      </div>

      {/* Language selectors */}
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <select value={sourceLang} onChange={(e) => { setSourceLang(e.target.value); setDetectedLang(null); }}
          style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 12, background: COLORS.white, cursor: 'pointer' }}>
          <option value="auto">Auto-detect{detectedLang ? ` (${detectedLang.language})` : ''}</option>
          {COMMON_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
        </select>

        <button onClick={handleSwapLanguages} title="Swap languages"
          style={{ background: COLORS.primaryLight, border: `1px solid ${COLORS.border}`, borderRadius: 6,
            width: 32, height: 32, cursor: sourceLang === 'auto' ? 'not-allowed' : 'pointer', fontSize: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: sourceLang === 'auto' ? 0.4 : 1 }}>
          ⇄
        </button>

        <select value={targetLang} onChange={(e) => setTargetLang(e.target.value)}
          style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 12, background: COLORS.white, cursor: 'pointer' }}>
          {COMMON_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
        </select>
      </div>

      {/* Quick language pills */}
      <div style={{ padding: '8px 16px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <span style={{ fontSize: 10, color: COLORS.textLight, width: '100%', marginBottom: 2 }}>Quick select target:</span>
        {displayedLangs.map(l => (
          <button key={l.code} onClick={() => setTargetLang(l.code)}
            style={{
              padding: '3px 8px', borderRadius: 12, fontSize: 10, cursor: 'pointer',
              border: targetLang === l.code ? `1px solid ${COLORS.primary}` : `1px solid ${COLORS.border}`,
              background: targetLang === l.code ? COLORS.primaryLight : COLORS.white,
              color: targetLang === l.code ? COLORS.primary : COLORS.text, fontWeight: targetLang === l.code ? 600 : 400,
            }}>
            {l.flag} {l.name}
          </button>
        ))}
        <button onClick={() => setShowAllLangs(!showAllLangs)}
          style={{ padding: '3px 8px', borderRadius: 12, fontSize: 10, cursor: 'pointer',
            border: `1px solid ${COLORS.border}`, background: COLORS.bg, color: COLORS.textLight }}>
          {showAllLangs ? '− Less' : `+ ${COMMON_LANGUAGES.length - 12} more`}
        </button>
      </div>

      {/* Source text */}
      <div style={{ padding: '12px 16px', flex: '0 0 auto' }}>
        <div style={{ position: 'relative' }}>
          <textarea
            value={sourceText}
            onChange={(e) => { if (e.target.value.length <= 10000) setSourceText(e.target.value); }}
            onKeyDown={handleKeyDown}
            placeholder="Enter text to translate... (Ctrl+Enter to translate)"
            rows={4}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
              fontSize: 13, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5,
              minHeight: 80, maxHeight: 200, boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
            <span style={{ fontSize: 10, color: charCount > 9000 ? '#e53e3e' : COLORS.textLight }}>
              {charCount.toLocaleString()} / 10,000
              {detecting && <span style={{ marginLeft: 8, color: COLORS.primary }}>detecting language...</span>}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              {sourceText && (
                <button onClick={handleClear} style={{
                  padding: '4px 10px', borderRadius: 4, border: `1px solid ${COLORS.border}`,
                  background: COLORS.white, fontSize: 11, cursor: 'pointer', color: COLORS.textLight,
                }}>Clear</button>
              )}
              <button onClick={handleTranslate} disabled={!sourceText.trim() || translating}
                style={{
                  padding: '6px 16px', borderRadius: 6, border: 'none',
                  background: !sourceText.trim() || translating ? '#cbd5e0' : COLORS.primary,
                  color: COLORS.white, fontSize: 12, fontWeight: 600, cursor: !sourceText.trim() || translating ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {translating ? (
                  <><span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: 12 }}>⟳</span> Translating...</>
                ) : '🌐 Translate'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '8px 16px', background: '#fff5f5', borderTop: `1px solid #fed7d7`, color: '#c53030', fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Translated text */}
      {translatedText && (
        <div style={{
          padding: '12px 16px', background: '#f0fff4', borderTop: `1px solid #c6f6d5`,
          flex: '1 1 auto', overflow: 'auto', maxHeight: 250,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.green }}>
              {COMMON_LANGUAGES.find(l => l.code === targetLang)?.flag} Translation ({COMMON_LANGUAGES.find(l => l.code === targetLang)?.name})
            </span>
            <button onClick={handleCopyTranslation}
              style={{
                padding: '3px 10px', borderRadius: 4, border: `1px solid ${copied ? COLORS.green : COLORS.border}`,
                background: copied ? '#c6f6d5' : COLORS.white, fontSize: 11, cursor: 'pointer',
                color: copied ? COLORS.green : COLORS.text,
              }}>
              {copied ? '✓ Copied' : '📋 Copy'}
            </button>
          </div>
          <div style={{
            fontSize: 13, lineHeight: 1.6, color: COLORS.dark, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {translatedText}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        padding: '8px 16px', borderTop: `1px solid ${COLORS.border}`, background: COLORS.bg,
        fontSize: 10, color: COLORS.textLight, textAlign: 'center',
      }}>
        Powered by Claude AI • Optimized for medical & scientific terminology
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
